import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, TileLayer, Polyline, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import { motion, AnimatePresence } from "framer-motion";
import {
  Map as MapIcon, Clock, TrendingUp, ArrowRight, X, DollarSign, LayoutList, MapPinned, CalendarDays,
  Play,
} from "lucide-react";
import { Dialog, DialogContent, DialogTrigger, DialogTitle } from "../ui/dialog";
import { api } from "../../lib/adventureApi";
import { useMarket } from "../../lib/market";
import { SeasonBar, SEASON_COLORS, SEASON_LABELS } from "./SeasonBar";

const CAT_COLORS = {
  trekking: "#E23D28", wellness: "#0E9E6E", motorcycle: "#F07813", cycling: "#7A4FD0",
  flying: "#1F74C4", culture: "#C98A1E",
};
const CAT_LABELS = {
  trekking: "Trekking", wellness: "Wellness", motorcycle: "Motorcycle", cycling: "Cycling",
  flying: "Flying", culture: "Cultural",
};
const METRICS = [
  { id: "duration", label: "Duration", icon: Clock },
  { id: "price", label: "Price", icon: DollarSign },
  { id: "difficulty", label: "Difficulty", icon: TrendingUp },
];
const MAP_CENTER = [34.0, 81.0];
const MAP_ZOOM = 5;
const HIMALAYA_BOUNDS = [[26.5, 71.0], [43.5, 91.5]];
const DEFAULT_BOUNDS = [[26.3, 80.3], [30.7, 90.2]];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// deterministic small offset so pills that share a midpoint don't stack perfectly
function spread([lat, lng], slug) {
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) & 0xffff;
  const ang = (h % 360) * (Math.PI / 180);
  const r = 0.04 + (h % 7) * 0.012;
  return [lat + Math.sin(ang) * r, lng + Math.cos(ang) * r];
}

function metricText(t, metric, priceFn) {
  if (metric === "price") return t.price_from ? priceFn(t.price_from) : "POA";
  if (metric === "difficulty") return (t.difficulty || "—").split(" ")[0];
  return t.days > 1 ? `${t.days}d` : "1d";
}

function pillIcon(label, color, active) {
  const bg = active ? "#171717" : color;
  const scale = active ? 1.14 : 1;
  const border = active ? "2px solid #fff" : "1px solid rgba(255,255,255,.7)";
  const shadow = active ? "0 8px 22px rgba(0,0,0,.5)" : "0 3px 10px rgba(0,0,0,.35)";
  return L.divIcon({
    className: "", iconSize: [0, 0], iconAnchor: [0, 0],
    html: `<div style="transform:translate(-50%,-50%) scale(${scale});transition:transform .12s;display:inline-flex;align-items:center;padding:4px 9px;border-radius:9999px;background:${bg};color:#fff;font-family:'JetBrains Mono',ui-monospace,monospace;font-size:11px;line-height:1;font-weight:700;white-space:nowrap;box-shadow:${shadow};border:${border};cursor:pointer">${label}</div>`,
  });
}

function FitBounds({ mobileView, bounds }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    const b = bounds || HIMALAYA_BOUNDS;
    map.options.maxBoundsViscosity = 0.4;
    map.setMaxBounds(L.latLngBounds(b).pad(0.35));
  }, [map, bounds]);
  // Fit bounds the first time the map is actually visible (on mobile it's hidden until "Show map").
  useEffect(() => {
    const visible = mobileView === "map" || window.innerWidth >= 768;
    if (!visible) return;
    const run = () => {
      map.invalidateSize();
      if (!fitted.current) { map.fitBounds(DEFAULT_BOUNDS, { padding: [24, 24] }); fitted.current = true; }
    };
    const t1 = setTimeout(run, 280);
    const t2 = setTimeout(run, 650);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [mobileView, map]);
  return null;
}

function SetMapRef({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

function ListCard({ t, color, catLabel, priceFn, active, seasonStatus, monthLabel, cardRef, onHover, onLeave, onOpen }) {
  const dim = seasonStatus === "blockout";
  return (
    <button ref={cardRef} type="button" data-testid={`planner-list-${t.slug}`}
      onMouseEnter={onHover} onMouseLeave={onLeave} onClick={onOpen}
      className={`w-full text-left flex gap-3 p-2.5 rounded-md border transition-all ${active ? "border-terracotta bg-terracotta/5 shadow-sm" : "border-transparent hover:bg-sand"} ${dim ? "opacity-55" : ""}`}>
      <div className="relative w-24 h-24 sm:w-28 sm:h-24 shrink-0 overflow-hidden rounded-md">
        <img src={t.hero_image} alt={t.name} loading="lazy" className="w-full h-full object-cover" />
        <span className="absolute top-1.5 left-1.5 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white" style={{ background: color }}>{catLabel}</span>
        {t.video_review_count > 0 && (
          <span className="absolute bottom-1.5 left-1.5 inline-flex items-center gap-1 bg-terracotta text-white font-mono text-[9px] px-1.5 py-0.5 rounded-sm" data-testid={`planner-video-badge-${t.slug}`}>
            <Play className="w-2.5 h-2.5" fill="currentColor" /> {t.video_review_count}
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1 py-0.5">
        <h3 className="font-display font-bold text-ink text-sm sm:text-base tracking-tight leading-snug line-clamp-2">{t.name}</h3>
        {t.subtitle && <p className="text-xs text-ink-secondary line-clamp-1 mt-0.5">{t.subtitle}</p>}
        <div className="flex items-center gap-3 mt-2 font-mono text-[10px] text-ink-secondary">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-moss" />{t.days > 1 ? `${t.days} days` : "Day tour"}</span>
          <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-moss" />{t.difficulty}</span>
        </div>
        <div className="mt-1.5 flex items-center gap-2 flex-wrap">
          <span className="font-mono text-[11px]">
            {t.price_from
              ? <span className="text-ink-secondary">From <span className="font-display font-extrabold text-terracotta text-sm">{priceFn(t.price_from)}</span></span>
              : <span className="text-ink-muted">Price on request</span>}
          </span>
          {seasonStatus && (
            <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide px-1.5 py-0.5 rounded-sm text-white" style={{ background: SEASON_COLORS[seasonStatus] }} data-testid={`planner-season-badge-${t.slug}`}>
              {seasonStatus === "blockout" ? `No departures · ${monthLabel}` : `${SEASON_LABELS[seasonStatus]} · ${monthLabel}`}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

function PlannerMap({ onNavigate }) {
  const { price } = useMarket();
  const [tours, setTours] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [metric, setMetric] = useState("price");
  const [hover, setHover] = useState(null);
  const [month, setMonth] = useState(null);          // 1-12 or null (Any)
  const [bookableOnly, setBookableOnly] = useState(false);
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'map' (mobile only)

  const mapRef = useRef(null);
  const listRef = useRef(null);
  const cardRefs = useRef({});
  const clearTimer = useRef(null);
  const flyTimer = useRef(null);

  useEffect(() => { api.get("/tours/map").then((r) => setTours(r.data)).catch(() => {}); }, []);

  const visible = useMemo(() => {
    let list = activeCat ? tours.filter((t) => t.category === activeCat) : tours;
    if (month && bookableOnly) list = list.filter((t) => (t.season_bar?.[month - 1]?.primary) !== "blockout");
    return list;
  }, [tours, activeCat, month, bookableOnly]);
  const statusOf = (t) => (month ? (t.season_bar?.[month - 1]?.primary || "shoulder") : null);
  const bySlug = useMemo(() => Object.fromEntries(tours.map((t) => [t.slug, t])), [tours]);
  const pillPos = useMemo(() => {
    const m = {};
    for (const t of tours) {
      const base = (t.route && t.route.length) ? t.route[Math.floor(t.route.length / 2)] : t.start;
      if (base) m[t.slug] = spread(base, t.slug);
    }
    return m;
  }, [tours]);

  const allBounds = useMemo(() => {
    const pts = [];
    for (const t of tours) {
      if (t.route && t.route.length) pts.push(...t.route);
      else if (t.start) pts.push(t.start);
      if (pillPos[t.slug]) pts.push(pillPos[t.slug]);
    }
    if (!pts.length) return null;
    let minLat = 90, minLng = 180, maxLat = -90, maxLng = -180;
    for (const [la, ln] of pts) { minLat = Math.min(minLat, la); maxLat = Math.max(maxLat, la); minLng = Math.min(minLng, ln); maxLng = Math.max(maxLng, ln); }
    return [[minLat, minLng], [maxLat, maxLng]];
  }, [tours, pillPos]);

  const selectCat = (c) => setActiveCat((prev) => (prev === c ? null : c));

  const onEnter = (slug, fromMap) => {
    clearTimeout(clearTimer.current);
    setHover(slug);
    if (fromMap) {
      const el = cardRefs.current[slug];
      if (el) el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    } else {
      // hovering a card → glide the map to that journey (also brings off-Nepal tours into view)
      clearTimeout(flyTimer.current);
      flyTimer.current = setTimeout(() => {
        const t = bySlug[slug];
        const map = mapRef.current;
        if (!t || !map) return;
        if (t.route && t.route.length > 1) map.flyToBounds(t.route, { padding: [70, 70], maxZoom: 9, duration: 0.6 });
        else if (t.start) map.flyTo(t.start, 7, { duration: 0.6 });
      }, 200);
    }
  };
  const onLeave = () => { clearTimeout(flyTimer.current); clearTimer.current = setTimeout(() => setHover(null), 120); };

  const openTour = (slug) => onNavigate(`/tours/${slug}`);

  const preview = hover ? bySlug[hover] : null;

  return (
    <div className="flex flex-col flex-1 min-h-0" data-testid="planner-body">
      {/* toolbar: category filters + metric toggle */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-sand-dark bg-white shrink-0 overflow-x-auto thin-scroll">
        <div className="flex items-center gap-1.5 shrink-0" data-testid="planner-filters">
          <button type="button" data-testid="planner-filter-all" onClick={() => setActiveCat(null)}
            className={`px-3 py-1.5 rounded-sm font-mono text-[11px] tracking-wide border transition-all whitespace-nowrap ${activeCat === null ? "bg-ink border-ink text-white" : "bg-white border-ink/10 text-ink-secondary hover:border-ink/30"}`}>
            All
          </button>
          {Object.keys(CAT_COLORS).map((c) => {
            const on = activeCat === c;
            return (
              <button key={c} type="button" data-testid={`planner-filter-${c}`} onClick={() => selectCat(c)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm font-mono text-[11px] tracking-wide border transition-all whitespace-nowrap ${on ? "text-white" : "bg-white border-ink/10 text-ink hover:border-ink/30"}`}
                style={on ? { background: CAT_COLORS[c], borderColor: CAT_COLORS[c] } : undefined}>
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: on ? "#fff" : CAT_COLORS[c] }} />
                {CAT_LABELS[c]}
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-1 shrink-0 bg-sand rounded-sm p-0.5" data-testid="planner-metric-toggle">
          {METRICS.map((m) => {
            const on = metric === m.id;
            const Icon = m.icon;
            return (
              <button key={m.id} type="button" data-testid={`planner-metric-${m.id}`} onClick={() => setMetric(m.id)}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-sm font-mono text-[10px] uppercase tracking-wide transition-all whitespace-nowrap ${on ? "bg-white text-ink shadow-sm" : "text-ink-muted hover:text-ink"}`}>
                <Icon className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* month / seasonality selector */}
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 border-b border-sand-dark bg-[#FDFBF7] shrink-0 overflow-x-auto thin-scroll" data-testid="planner-month-bar">
        <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted shrink-0 flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-terracotta" /> When
        </span>
        <div className="flex items-center gap-1 shrink-0">
          <button type="button" data-testid="planner-month-any" onClick={() => setMonth(null)}
            className={`px-2.5 py-1 rounded-sm font-mono text-[10px] uppercase tracking-wide border transition-all ${month === null ? "bg-ink border-ink text-white" : "bg-white border-ink/10 text-ink-secondary hover:border-ink/30"}`}>
            Any
          </button>
          {MONTHS.map((mo, i) => {
            const on = month === i + 1;
            return (
              <button key={mo} type="button" data-testid={`planner-month-${i + 1}`} onClick={() => setMonth(on ? null : i + 1)}
                className={`px-2 py-1 rounded-sm font-mono text-[10px] uppercase tracking-wide border transition-all ${on ? "bg-terracotta border-terracotta text-white" : "bg-white border-ink/10 text-ink-secondary hover:border-ink/30"}`}>
                {mo}
              </button>
            );
          })}
        </div>
        {month && (
          <>
            <div className="ml-1 shrink-0 flex items-center gap-2">
              {["best", "shoulder", "blockout"].map((k) => (
                <span key={k} className="hidden md:flex items-center gap-1 font-mono text-[9px] uppercase tracking-wide text-ink-muted">
                  <span className="w-2.5 h-2.5 rounded-[2px]" style={{ background: SEASON_COLORS[k] }} />{SEASON_LABELS[k]}
                </span>
              ))}
            </div>
            <button type="button" data-testid="planner-bookable-toggle" onClick={() => setBookableOnly((v) => !v)}
              className={`ml-auto shrink-0 flex items-center gap-1.5 px-2.5 py-1 rounded-sm font-mono text-[10px] uppercase tracking-wide border transition-all ${bookableOnly ? "bg-moss border-moss text-white" : "bg-white border-ink/10 text-ink-secondary hover:border-ink/30"}`}>
              <span className={`w-2 h-2 rounded-full ${bookableOnly ? "bg-white" : "bg-moss"}`} /> Bookable only
            </button>
          </>
        )}
      </div>

      {/* split body */}
      <div className="flex flex-1 min-h-0 relative">
        {/* LEFT — filtered thumbnail list */}
        <aside ref={listRef}
          className={`${mobileView === "list" ? "flex" : "hidden"} md:flex flex-col w-full md:w-[380px] lg:w-[420px] shrink-0 border-r border-sand-dark bg-[#FDFBF7] overflow-y-auto thin-scroll`}
          data-testid="planner-list">
          <div className="px-3 pt-3 pb-1 font-mono text-[10px] uppercase tracking-widest text-ink-muted">
            {visible.length} {visible.length === 1 ? "journey" : "journeys"}
          </div>
          <div className="flex flex-col gap-1 p-2">
            {visible.map((t) => (
              <ListCard key={t.slug} t={t} priceFn={price}
                color={CAT_COLORS[t.category] || "#E23D28"} catLabel={CAT_LABELS[t.category] || t.category}
                active={hover === t.slug}
                seasonStatus={statusOf(t)} monthLabel={month ? MONTHS[month - 1] : ""}
                cardRef={(el) => { cardRefs.current[t.slug] = el; }}
                onHover={() => onEnter(t.slug, false)} onLeave={onLeave}
                onOpen={() => openTour(t.slug)} />
            ))}
            {visible.length === 0 && (
              <p className="text-sm text-ink-muted p-4 text-center">No journeys match this filter.</p>
            )}
          </div>
        </aside>

        {/* RIGHT — map */}
        <div className={`${mobileView === "map" ? "block" : "hidden"} md:block relative flex-1 min-h-0`}>
          <MapContainer center={MAP_CENTER} zoom={MAP_ZOOM} minZoom={4} maxZoom={12}
            style={{ height: "100%", width: "100%" }} zoomControl={false} attributionControl={false}
            scrollWheelZoom={true} className="z-0">
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}" maxZoom={16} />
            <FitBounds mobileView={mobileView} bounds={allBounds} />
            <SetMapRef mapRef={mapRef} />

            {/* routes */}
            {visible.map((t) => {
              if (!(t.route && t.route.length > 1)) return null;
              const st = statusOf(t);
              const color = st ? SEASON_COLORS[st] : (CAT_COLORS[t.category] || "#E23D28");
              const isHot = hover === t.slug;
              const blocked = st === "blockout";
              const faded = (hover && !isHot) || (blocked && !isHot);
              return <Polyline key={`${t.slug}-${st || "cat"}`} positions={t.route}
                eventHandlers={{ mouseover: () => onEnter(t.slug, true), mouseout: onLeave, click: () => openTour(t.slug) }}
                pathOptions={{ color, weight: isHot ? 7 : 4, opacity: faded ? 0.15 : isHot ? 1 : 0.75, lineCap: "round", lineJoin: "round" }} />;
            })}

            {/* one metric pill per visible journey */}
            {visible.map((t) => {
              const pos = pillPos[t.slug];
              if (!pos) return null;
              const active = hover === t.slug;
              const st = statusOf(t);
              const color = st ? SEASON_COLORS[st] : (CAT_COLORS[t.category] || "#E23D28");
              return (
                <Marker key={`${t.slug}-${metric}-${st || "cat"}-${active}`} position={pos}
                  icon={pillIcon(metricText(t, metric, price), color, active)}
                  opacity={st === "blockout" && !active ? 0.5 : 1}
                  zIndexOffset={active ? 3000 : (st === "best" ? 1500 : 0)}
                  eventHandlers={{
                    mouseover: () => onEnter(t.slug, true), mouseout: onLeave,
                    click: () => openTour(t.slug),
                  }} />
              );
            })}
          </MapContainer>

          {/* hover preview mini-card */}
          <AnimatePresence>
            {preview && (
              <motion.button type="button" key={preview.slug}
                initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.16 }}
                onClick={() => openTour(preview.slug)}
                onMouseEnter={() => onEnter(preview.slug, false)} onMouseLeave={onLeave}
                className="absolute bottom-3 left-3 z-[1000] w-[300px] max-w-[calc(100%-1.5rem)] text-left bg-[#FDFBF7]/98 backdrop-blur-xl border border-ink/10 rounded-md overflow-hidden shadow-2xl"
                data-testid="planner-preview">
                <div className="relative h-28">
                  <img src={preview.hero_image} alt={preview.name} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/55 to-transparent" />
                  <span className="absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm text-white" style={{ background: CAT_COLORS[preview.category] || "#E23D28" }}>{CAT_LABELS[preview.category] || preview.category}</span>
                  <h3 className="absolute bottom-2 left-2 right-2 font-display font-bold text-white text-sm leading-snug line-clamp-2">{preview.name}</h3>
                </div>
                <div className="flex items-center gap-3 px-3 py-2 font-mono text-[10px] text-ink-secondary">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-moss" />{preview.days > 1 ? `${preview.days} days` : "Day tour"}</span>
                  <span className="flex items-center gap-1"><TrendingUp className="w-3 h-3 text-moss" />{preview.difficulty}</span>
                  <span className="ml-auto flex items-center gap-1 text-terracotta font-semibold">Open <ArrowRight className="w-3 h-3" /></span>
                </div>
                {preview.season_bar && (
                  <div className="px-3 pb-2.5 pt-0.5">
                    <SeasonBar bar={preview.season_bar} size="sm" activeMonth={month} />
                  </div>
                )}
              </motion.button>
            )}
          </AnimatePresence>

          {/* count chip */}
          <div className="absolute top-3 right-3 z-[1000] flex items-center gap-1.5 bg-[#FDFBF7]/95 backdrop-blur-md border border-ink/10 rounded-sm px-3 py-1.5 font-mono text-[11px] text-ink-secondary" data-testid="planner-count">
            <MapIcon className="w-3.5 h-3.5 text-terracotta" /> {visible.length} on map
          </div>
        </div>

        {/* mobile show map / show list toggle */}
        <button type="button" data-testid="planner-mobile-toggle"
          onClick={() => setMobileView((v) => (v === "list" ? "map" : "list"))}
          className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 z-[1050] flex items-center gap-2 bg-ink hover:bg-ink/90 text-white px-5 h-11 rounded-full text-sm font-semibold shadow-2xl transition-colors">
          {mobileView === "list"
            ? <><MapPinned className="w-4 h-4" /> Show map</>
            : <><LayoutList className="w-4 h-4" /> Show list</>}
        </button>
      </div>
    </div>
  );
}

export default function MapPlanner({ triggerClassName, triggerLabel = "Map Planner" }) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (path) => { setOpen(false); navigate(path); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="map-planner-btn" className={triggerClassName}>
          <MapIcon className="w-4 h-4" /> {triggerLabel}
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-[1500px] w-[97vw] h-[93vh] p-0 gap-0 overflow-hidden rounded-md flex flex-col [&>button]:hidden"
        data-testid="map-planner-modal">
        <DialogTitle className="sr-only">Map Planner</DialogTitle>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-sand-dark bg-white shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <MapIcon className="w-4 h-4 text-terracotta shrink-0" />
            <div className="min-w-0">
              <p className="font-display font-bold text-base tracking-tight leading-none">Map Planner</p>
              <p className="text-[11px] text-ink-muted mt-0.5 truncate">Browse the journeys, hover to trace them on the map, tap to step inside.</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={() => go("/build")} data-testid="planner-build-btn"
              className="hidden sm:flex items-center gap-1.5 bg-terracotta hover:bg-terracotta-hover text-white px-3.5 h-9 rounded-sm text-sm font-semibold tracking-wide transition-colors">
              Build & price on the map <ArrowRight className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} data-testid="planner-close-btn" aria-label="Close map planner"
              className="w-9 h-9 flex items-center justify-center rounded-sm bg-sand hover:bg-sand-dark text-ink transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        {open && <PlannerMap onNavigate={go} />}
      </DialogContent>
    </Dialog>
  );
}
